import * as authApi from "@/api/auth";
import { type PreferencesUpdateData, updatePreferences as updatePrefsApi } from "@/api/users";
import type { User } from "@/types";
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from "react";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  updatePreferences: (prefs: PreferencesUpdateData) => Promise<void>;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
}

type AuthAction =
  | { type: "set_user"; user: User }
  | { type: "clear_user" }
  | { type: "restore_failed" };

function authReducer(_state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "set_user":
      return { user: action.user, isLoading: false };
    case "clear_user":
      return { user: null, isLoading: false };
    case "restore_failed":
      return { user: null, isLoading: false };
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, { user: null, isLoading: true });

  const isAuthenticated = state.user !== null;

  // Try to restore session on mount using stored refresh token
  useEffect(() => {
    const restore = async () => {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        dispatch({ type: "restore_failed" });
        return;
      }

      try {
        const tokens = await authApi.refreshTokens(refreshToken);
        localStorage.setItem("access_token", tokens.access_token);
        localStorage.setItem("refresh_token", tokens.refresh_token);
        const currentUser = await authApi.fetchCurrentUser();
        dispatch({ type: "set_user", user: currentUser });
      } catch {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        dispatch({ type: "restore_failed" });
      }
    };

    restore();
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const tokens = await authApi.login(username, password);
    localStorage.setItem("access_token", tokens.access_token);
    localStorage.setItem("refresh_token", tokens.refresh_token);
    const currentUser = await authApi.fetchCurrentUser();
    dispatch({ type: "set_user", user: currentUser });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    dispatch({ type: "clear_user" });
  }, []);

  const updatePreferences = useCallback(async (prefs: PreferencesUpdateData) => {
    const updated = await updatePrefsApi(prefs);
    dispatch({ type: "set_user", user: updated });
  }, []);

  const value = useMemo(
    () => ({
      user: state.user,
      isAuthenticated,
      isLoading: state.isLoading,
      login,
      logout,
      updatePreferences,
    }),
    [state.user, isAuthenticated, state.isLoading, login, logout, updatePreferences],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
